package one.healthium.med.presentation.navigation.home.compose

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.width
import androidx.compose.material3.Icon
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import ifaandroid.apps.med.composeapp.generated.resources.Res
import ifaandroid.apps.med.composeapp.generated.resources.ic_calendar_clock
import ifaandroid.apps.med.composeapp.generated.resources.ic_scroll_content
import ifaandroid.apps.med.composeapp.generated.resources.ic_speechbubble_text
import ifaandroid.apps.med.composeapp.generated.resources.ic_user
import kotlinx.coroutines.launch
import loco.MAIN_NAVIGATION_CHAT_LABEL
import loco.MAIN_NAVIGATION_DAILY_LABEL
import loco.MAIN_NAVIGATION_INSIGHTS_LABEL
import loco.MAIN_NAVIGATION_ME_LABEL
import one.healthium.med.presentation.navigation.home.HomeComponent
import one.healthium.tasks.ui.DailyTasksBadge
import one.healthium.ui.compose.components.AppNavigationBarMinHeight
import one.healthium.ui.compose.components.AppNavigationRail
import one.healthium.ui.compose.components.AppNavigationRailItem
import one.healthium.ui.compose.components.LocalGlobalProgressIndicator
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.gray_17
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@Composable
internal fun NavigationRail(component: HomeComponent) {
    val stack by component.stack.subscribeAsState()
    val activeComponent = stack.active.instance
    val scope = rememberCoroutineScope()
    val globalProgressIndicator = LocalGlobalProgressIndicator.current

    AppNavigationRail {
        // DailyTasks
        AppNavigationRailItem(
            modifier = Modifier.width(AppNavigationBarMinHeight).testTag("nav_tasks"),
            icon = {
                Box {
                    Icon(
                        painter = painterResource(Res.drawable.ic_calendar_clock),
                        tint = accentColor(activeComponent is HomeComponent.Child.DailyTasksChild),
                        contentDescription = null,
                    )
                    DailyTasksBadge(
                        modifier = Modifier
                            .align(Alignment.TopEnd)
                            .offset(x = 4.dp, y = (-4).dp),
                        borderStroke = BorderStroke(width = 1.dp, color = LocalContentColor.current),
                    )
                }
            },
            label = {
                Text(
                    text = stringResource(loco.Res.string.MAIN_NAVIGATION_DAILY_LABEL),
                )
            },
            selected = activeComponent is HomeComponent.Child.DailyTasksChild,
            onClick = component::onDailyTasksTabClicked,
        )

        // Insights
        AppNavigationRailItem(
            modifier = Modifier.width(AppNavigationBarMinHeight).testTag("nav_insights"),
            icon = {
                Icon(
                    painter = painterResource(Res.drawable.ic_scroll_content),
                    tint = accentColor(activeComponent is HomeComponent.Child.InsightsChild),
                    contentDescription = null,
                )
            },
            label = {
                Text(
                    text = stringResource(loco.Res.string.MAIN_NAVIGATION_INSIGHTS_LABEL),
                )
            },
            selected = activeComponent is HomeComponent.Child.MainContentChild,
            onClick = component::onMainContentTabClicked,
        )

        // Chat
        AppNavigationRailItem(
            modifier = Modifier.width(AppNavigationBarMinHeight).testTag("nav_chat"),
            icon = {
                Icon(
                    painter = painterResource(Res.drawable.ic_speechbubble_text),
                    tint = accentColor(activeComponent is HomeComponent.Child.ChatChild),
                    contentDescription = null,
                )
            },
            label = {
                Text(
                    text = stringResource(loco.Res.string.MAIN_NAVIGATION_CHAT_LABEL),
                )
            },
            selected = activeComponent is HomeComponent.Child.ChatChild,
            onClick = {
                scope.launch {
                    globalProgressIndicator.value = true
                    component.onChatTabClicked()
                    globalProgressIndicator.value = false
                }
            },
        )

        // Profile
        AppNavigationRailItem(
            modifier = Modifier.width(AppNavigationBarMinHeight).testTag("nav_profile"),
            icon = {
                Icon(
                    painter = painterResource(Res.drawable.ic_user),
                    tint = accentColor(activeComponent is HomeComponent.Child.ProfileChild),
                    contentDescription = null,
                )
            },
            label = {
                Text(
                    text = stringResource(loco.Res.string.MAIN_NAVIGATION_ME_LABEL),
                )
            },
            selected = activeComponent is HomeComponent.Child.ProfileChild,
            onClick = component::onProfileTabClicked,
        )
    }
}

@Composable
private fun accentColor(active: Boolean) = if (active) {
    AppTheme.colors.primary
} else {
    gray_17
}
